import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Option } from '@weave-design/menu';
import { useGlobalization } from '@Translations';
import { StyledMenu } from './DataSourceFilter.styles';
import Dropdown from '@weave-design/dropdown';
import { FormControl, InputLabel } from '@mui/material';
import { GridFilterInputValueProps } from '@innovyze/stylovyze';
import { useConfigurationsPagination, useConfigurationsSearch } from '@Hooks';
import { useSelector } from 'react-redux';
import { getValue } from '@Selectors';
import { useDispatch } from 'react-redux';
import { headerCheckboxCheckedChange, rowSelectionModelChange } from '@Actions';
import {
	DataSourceTypesLabels,
	PhysicalDataSourceType,
	VirtualDataSourceType,
} from '@Utils/constants';

export const DataSourceFilterMuix = ({
	gridFilterProps,
	onDataSourceTypesChange,
}: {
	gridFilterProps: GridFilterInputValueProps;
	onDataSourceTypesChange: () => void;
}) => {
	const { t } = useGlobalization();
	const { dataSourceTypes, changeDataSourceTypes } =
		useConfigurationsSearch();
	const [, fetchConfigurationsWithCriteria] = useConfigurationsPagination();
	const {
		configurations: { sensorTypes, searchValue, physicalOrVirtual },
	} = useSelector(getValue);

	const { applyValue, item } = gridFilterProps;

	const DATA_SOURCE_TYPE_LIST = useMemo(
		() => [
			...Object.values(PhysicalDataSourceType).map(val => ({
				key: val,
				label: val,
			})),
			...Object.values(VirtualDataSourceType)
				.filter(val => val !== VirtualDataSourceType.EMAGIN)
				.map(val => ({
					key: val,
					// @ts-ignore
					label: DataSourceTypesLabels[val],
				})),
		],
		[],
	);

	const dropdownRef = useRef<HTMLDivElement>(null);
	const [selectedDataSourceTypes, setDataSourceTypes] = useState<string[]>(
		dataSourceTypes || [],
	);

	const [dropdownPlaceholder, setDropdownPlaceholder] = useState<string>(
		t('All'),
	);

	const dispatch = useDispatch();

	const [isMenuOpen, setIsMenuOpen] = useState(false);

	const onDataSourceTypeChange = (types: string[]) => {
		setDataSourceTypes(types);
		changeDataSourceTypes(types);
		dispatch(rowSelectionModelChange([]));
		dispatch(headerCheckboxCheckedChange(false));
		fetchConfigurationsWithCriteria(
			1,
			searchValue,
			sensorTypes,
			types,
			physicalOrVirtual,
			true,
		);
		onDataSourceTypesChange();
	};

	const onDataSourceTypeSelect = (sensorType: string) => {
		const existsSensorType = selectedDataSourceTypes.includes(sensorType);
		let newSelectedTypes: string[] = [];
		if (existsSensorType) {
			newSelectedTypes = selectedDataSourceTypes.filter(
				sType => sType !== sensorType && sType !== 'All',
			);
		} else {
			newSelectedTypes = [...selectedDataSourceTypes, sensorType];
		}

		if (newSelectedTypes.length === DATA_SOURCE_TYPE_LIST.length) {
			newSelectedTypes.push('All');
		}
		onDataSourceTypeChange(newSelectedTypes);
	};

	const selectAllSourceTypes = () => {
		if (selectedDataSourceTypes.includes('All')) {
			setDataSourceTypes([]);
			onDataSourceTypeChange([]);
			return;
		}
		const newSensorTypes = [
			...DATA_SOURCE_TYPE_LIST.map(type => type.key),
			'All',
		];
		setDataSourceTypes(newSensorTypes);
		onDataSourceTypeChange(newSensorTypes);
	};

	useEffect(() => {
		const numberOfSelectedTypes = selectedDataSourceTypes.length;
		const totalSensorTypes = DATA_SOURCE_TYPE_LIST.length;
		let dropdownValue = '';
		if (numberOfSelectedTypes === 0) {
			dropdownValue = t('All types');
		}
		if (numberOfSelectedTypes === 1) {
			dropdownValue =
				// @ts-ignore
				DataSourceTypesLabels[selectedDataSourceTypes[0]] ??
				selectedDataSourceTypes[0];
		}

		if (
			numberOfSelectedTypes > 1 &&
			numberOfSelectedTypes < totalSensorTypes
		) {
			dropdownValue = t('Multiple');
		}

		if (numberOfSelectedTypes >= totalSensorTypes) {
			dropdownValue = t('All');
		}

		setDropdownPlaceholder(dropdownValue);

		applyValue({
			...item,
			value: dropdownValue || '',
		});
	}, [selectedDataSourceTypes]);

	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			if (
				dropdownRef.current &&
				!dropdownRef.current.contains(event.target as Node)
			) {
				setIsMenuOpen(false);
			}
		};

		document.addEventListener('mousedown', handleClickOutside);

		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
		};
	}, []);

	return (
		<FormControl variant="standard" ref={dropdownRef}>
			<InputLabel shrink>{t('Value')}</InputLabel>
			<Dropdown
				value={dropdownPlaceholder}
				onClick={() => setIsMenuOpen(!isMenuOpen)}
				stylesheet={styles => ({
					...styles,
					input: {
						...(styles.input as Record<string, unknown>),
						fontWeight: 'normal',
					},
					dropdownWrapper: {
						...(styles.dropdownWrapper as Record<string, unknown>),
						top: '12px',
					},
				})}
			/>
			{isMenuOpen ? (
				<StyledMenu
					multiple
					checkmark
					height="500px"
					selected={selectedDataSourceTypes}>
					<Option id="All" onClick={selectAllSourceTypes}>
						{t('All')}
					</Option>
					{DATA_SOURCE_TYPE_LIST.map(sourceType => (
						<Option
							key={sourceType.key}
							id={sourceType.key}
							onClick={() =>
								onDataSourceTypeSelect(sourceType.key)
							}>
							{t(sourceType.label)}
						</Option>
					))}
				</StyledMenu>
			) : null}
		</FormControl>
	);
};
